
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { UserPlus, X, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Department } from "@/types/department";

interface AddEmployeeModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: (employee: {
    name: string;
    email: string;
    position: string;
    phone: string;
    status: string;
    start_date: string;
    department_id?: string;
  }) => void;
  departments?: Department[];
}

const AddEmployeeModal = ({ isOpen, onOpenChange, onSave, departments = [] }: AddEmployeeModalProps) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [position, setPosition] = useState("");
  const [phone, setPhone] = useState("+63");
  const [status, setStatus] = useState("active");
  const [departmentId, setDepartmentId] = useState("");
  
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Reset form on open/close
  useEffect(() => {
    if (isOpen) {
      setName("");
      setEmail("");
      setPosition("");
      setPhone("+63"); // Reset to +63 prefix
      setStatus("active");
      setDepartmentId("");
    }
  }, [isOpen]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    // Handle 09 format conversion to +63 format
    if (value.startsWith("09") && value.length <= 11) {
      // Remove the +63 prefix if user is typing 09
      setPhone(value);
    } 
    // Make sure +63 prefix is always there otherwise
    else if (!value.startsWith("+63") && value !== "") {
      setPhone("+63" + value.replace(/^\+63/, ""));
    }
    // Just set the value if it's already in the correct format or empty
    else {
      setPhone(value);
    }
  };

  const formatPhoneForSubmission = (phoneNumber: string) => {
    // Convert 09 format to +63 format for submission
    if (phoneNumber.startsWith("09")) {
      return "+63" + phoneNumber.slice(2);
    }
    return phoneNumber;
  };
  
  const handleSave = () => {
    if (!name || !email || !position) {
      toast({
        title: "Missing Fields",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (onSave) {
        const currentDate = new Date().toISOString().split('T')[0]; // Get current date in YYYY-MM-DD format
        onSave({
          name,
          email,
          position,
          phone: formatPhoneForSubmission(phone),
          status,
          start_date: currentDate,
          department_id: departmentId || undefined
        });
      }
    } catch (error) {
      console.error("Error saving employee:", error);
      toast({
        title: "Error",
        description: "Failed to save employee",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            Add New Employee
          </DialogTitle>
          <DialogDescription>
            Add a new employee to the selected division
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              placeholder="Juan Dela Cruz"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              placeholder="juan.delacruz@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="position">Position *</Label>
            <Input
              id="position"
              placeholder="Senior Tourism Officer"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
            />
          </div>

          {departments && departments.length > 0 && (
            <div className="grid gap-2">
              <Label htmlFor="department">Department</Label>
              <select
                id="department"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value)}
              >
                <option value="">Select Department</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              placeholder="e.g., +63912345678 or 09123456789"
              value={phone}
              onChange={handlePhoneChange}
            />
            <p className="text-xs text-muted-foreground">
              Enter in +63 format or 09 format (e.g., +639123456789 or 09123456789)
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="status">Status *</Label>
            <select
              id="status"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="active">Active</option>
              <option value="on-leave">On Leave</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSubmitting}>
            <Check className="h-4 w-4 mr-2" />
            {isSubmitting ? "Saving..." : "Save Employee"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddEmployeeModal;
